import React, { useEffect, useState } from 'react'
import { Routes, Route, Navigate, useParams } from 'react-router-dom'
import { GoogleOAuthProvider } from '@react-oauth/google'
import LoginPage from './pages/LoginPage'
import RegisterPage from './pages/RegisterPage'
import DashboardPage from './pages/DashboardPage'
import HomePage from './pages/HomePage'
import ServicesPage from './pages/ServicesPage'
import AppointmentsPage from './pages/AppointmentsPage'
import ProfilePage from './pages/ProfilePage'
import EditProfilePage from './pages/EditProfilePage'
import SchedulingWizardPage from './pages/SchedulingWizardPage'
import ProfessionalDashboardPage from './pages/ProfessionalDashboardPage'
import ProfessionalAgendaPage from './pages/ProfessionalAgendaPage'
import ProfessionalMenuPage from './pages/ProfessionalMenuPage'
import ClientsPage from './pages/ClientsPage'
import ClientDetailPage from './pages/ClientDetailPage'
import ClientFormPage from './pages/ClientFormPage'
import KanbanBoardPage from './pages/KanbanBoardPage'
import ComingSoonPage from './pages/ComingSoonPage'
import { useAuthStore } from './stores/authStore'
import { useViewModeStore } from './stores/viewModeStore'
import UpdateNotification from './components/UpdateNotification'
import GlobalThemeLoader from './components/GlobalThemeLoader'
import FloatingWhatsAppButton from './components/FloatingWhatsAppButton'
import ThemeSettingsPage from './pages/ThemeSettingsPage'
import { initializeTenantTheme } from './utils/themeUtils'
import { ThemeProvider } from './contexts/ThemeContext'
import { AppSettingsProvider } from './contexts/AppSettingsContext'

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID

// Helper function to check if user is professional
const isProfessionalRole = (role) => {
  return ['PROFISSIONAL', 'ATENDENTE', 'GESTOR'].includes(role);
}

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuthStore()
  const { tenantSlug } = useParams()
  const loginPath = tenantSlug ? `/${tenantSlug}/login` : '/login'
  return isAuthenticated ? children : <Navigate to={loginPath} replace />
}

// Role-based Route Component with View Mode Support
const RoleBasedRoute = ({ children, allowedRoles, professionalOnly = false }) => {
  const { isAuthenticated, user } = useAuthStore()
  const { currentMode } = useViewModeStore()
  const { tenantSlug } = useParams()

  if (!isAuthenticated) {
    const loginPath = tenantSlug ? `/${tenantSlug}/login` : '/login'
    return <Navigate to={loginPath} replace />
  }

  // For professional-only routes, check if user is professional and in professional mode
  if (professionalOnly) {
    const isProf = isProfessionalRole(user?.role)
    const inProfMode = currentMode === 'professional'

    if (!isProf || !inProfMode) {
      const dashboardPath = tenantSlug ? `/${tenantSlug}/dashboard` : '/dashboard'
      return <Navigate to={dashboardPath} replace />
    }

    return children
  }

  // For role-based routes, check actual user roles
  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    // If user role is not allowed, redirect to appropriate dashboard
    const redirectPath = isProfessionalRole(user?.role) ?
      (tenantSlug ? `/${tenantSlug}/professional/dashboard` : "/professional/dashboard") :
      (tenantSlug ? `/${tenantSlug}/dashboard` : "/dashboard")
    return <Navigate to={redirectPath} replace />
  }

  return children
}

// Guest Route Component - Auto-logins as guest if not authenticated
const GuestRoute = ({ children }) => {
  const { isAuthenticated, loginAsGuest, isLoading } = useAuthStore()
  const [isInitializing, setIsInitializing] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let isMounted = true

    const initGuestSession = async () => {
      // If already authenticated, no need to login as guest
      if (isAuthenticated) {
        if (isMounted) setIsInitializing(false)
        return
      }

      // Wait for any existing loading to complete
      if (isLoading) {
        return
      }

      try {
        console.log('GuestRoute: Initiating guest login...')
        const guestUser = await loginAsGuest()
        console.log('GuestRoute: Guest login successful', { guestUser, isGuest: guestUser?.isGuest, role: guestUser?.role })
        if (isMounted) {
          setError(null)
          setIsInitializing(false)
        }
      } catch (err) {
        console.error('GuestRoute: Failed to initialize guest session:', err)
        if (isMounted) {
          setError(err.message)
          setIsInitializing(false)
        }
      }
    }

    initGuestSession()

    return () => {
      isMounted = false
    }
  }, [isAuthenticated, loginAsGuest, isLoading])

  // Show loading while initializing or during auth loading
  if (isInitializing || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-light dark:bg-brand-dark">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Carregando...</p>
        </div>
      </div>
    )
  }

  // Show error if guest login failed
  if (error && !isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-light dark:bg-brand-dark">
        <div className="text-center p-6">
          <p className="text-red-600 mb-4">Erro ao carregar: {error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary text-white rounded-lg"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    )
  }

  return children
}

function App() {
  const { isAuthenticated, validateStoredToken, user } = useAuthStore()
  const { currentMode } = useViewModeStore()

  // Validate token on app startup
  useEffect(() => {
    validateStoredToken()
  }, [])

  // Mobile version checking is now handled by PWA service worker in UpdateNotification component
  // No need for manual version checking in mobile app

  // Initialize tenant theme on app startup
  useEffect(() => {
  }, [])

  // Smart redirect based on user role and view mode
  const getDefaultRedirectPath = (tenantSlug = null) => {
    if (!isAuthenticated || !user) {
      return tenantSlug ? `/${tenantSlug}/login` : '/login'
    }
    
    // If user is a professional, check their current view mode
    if (isProfessionalRole(user.role)) {
      const basePath = currentMode === 'client' ? '/dashboard' : '/professional/dashboard'
      return tenantSlug ? `/${tenantSlug}${basePath}` : basePath
    }
    
    // Regular clients always go to client dashboard
    return tenantSlug ? `/${tenantSlug}/dashboard` : '/dashboard'
  }

  // Tenant Root Route Component
  const TenantRootRoute = () => {
    const { tenantSlug } = useParams();
    return isAuthenticated ? <Navigate to={getDefaultRedirectPath(tenantSlug)} replace /> : <LoginPage />;
  };

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <ThemeProvider>
        <AppSettingsProvider>
          <div className="h-full w-full">
            <GlobalThemeLoader />
            <UpdateNotification />
            <FloatingWhatsAppButton />
            <Routes>
        <Route
          path="/"
          element={isAuthenticated ? <Navigate to={getDefaultRedirectPath()} replace /> : <LoginPage />}
        />

        {/* Auth Routes - Public, no authentication required */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/:tenantSlug/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/:tenantSlug/register" element={<RegisterPage />} />

        {/* Guest Booking Routes - Auto-login as guest if not authenticated */}
        {/* /agendar shows the service browsing page (HomePage) */}
        <Route path="/agendar" element={
          <GuestRoute>
            <HomePage />
          </GuestRoute>
        } />
        <Route path="/:tenantSlug/agendar" element={
          <GuestRoute>
            <HomePage />
          </GuestRoute>
        } />
        {/* /agendar/wizard shows the scheduling wizard after services are selected */}
        <Route path="/agendar/wizard" element={
          <GuestRoute>
            <SchedulingWizardPage />
          </GuestRoute>
        } />
        <Route path="/:tenantSlug/agendar/wizard" element={
          <GuestRoute>
            <SchedulingWizardPage />
          </GuestRoute>
        } />

        {/* Client Routes - Accessible by clients OR professionals in client mode */}
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        } />
        <Route path="/:tenantSlug/dashboard" element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        } />
        <Route path="/cart" element={
          <ProtectedRoute>
            <ServicesPage />
          </ProtectedRoute>
        } />
        <Route path="/:tenantSlug/cart" element={
          <ProtectedRoute>
            <ServicesPage />
          </ProtectedRoute>
        } />
        <Route path="/appointments" element={
          <ProtectedRoute>
            <AppointmentsPage />
          </ProtectedRoute>
        } />
        <Route path="/:tenantSlug/appointments" element={
          <ProtectedRoute>
            <AppointmentsPage />
          </ProtectedRoute>
        } />
        <Route path="/scheduling-wizard" element={
          <ProtectedRoute>
            <SchedulingWizardPage />
          </ProtectedRoute>
        } />
        <Route path="/:tenantSlug/scheduling-wizard" element={
          <ProtectedRoute>
            <SchedulingWizardPage />
          </ProtectedRoute>
        } />
        <Route path="/kanban" element={
          <RoleBasedRoute allowedRoles={['ATENDENTE', 'GESTOR']} professionalOnly={true}>
            <KanbanBoardPage />
          </RoleBasedRoute>
        } />
        <Route path="/:tenantSlug/kanban" element={
          <RoleBasedRoute allowedRoles={['ATENDENTE', 'GESTOR']} professionalOnly={true}>
            <KanbanBoardPage />
          </RoleBasedRoute>
        } />
        
        {/* Professional Routes - Only accessible by salon staff in professional mode */}
        <Route path="/professional/dashboard" element={
          <RoleBasedRoute professionalOnly={true}>
            <ProfessionalDashboardPage />
          </RoleBasedRoute>
        } />
        <Route path="/:tenantSlug/professional/dashboard" element={
          <RoleBasedRoute professionalOnly={true}>
            <ProfessionalDashboardPage />
          </RoleBasedRoute>
        } />
        <Route path="/professional/agenda" element={
          <RoleBasedRoute professionalOnly={true}>
            <ProfessionalAgendaPage />
          </RoleBasedRoute>
        } />
        <Route path="/:tenantSlug/professional/agenda" element={
          <RoleBasedRoute professionalOnly={true}>
            <ProfessionalAgendaPage />
          </RoleBasedRoute>
        } />
        <Route path="/professional/menu" element={
          <RoleBasedRoute professionalOnly={true}>
            <ProfessionalMenuPage />
          </RoleBasedRoute>
        } />
        <Route path="/:tenantSlug/professional/menu" element={
          <RoleBasedRoute professionalOnly={true}>
            <ProfessionalMenuPage />
          </RoleBasedRoute>
        } />
        <Route path="/professional/theme" element={
          <RoleBasedRoute professionalOnly={true}>
            <ThemeSettingsPage />
          </RoleBasedRoute>
        } />
        <Route path="/:tenantSlug/professional/theme" element={
          <RoleBasedRoute professionalOnly={true}>
            <ThemeSettingsPage />
          </RoleBasedRoute>
        } />
        <Route path="/professional/kanban" element={
          <RoleBasedRoute allowedRoles={['ATENDENTE', 'GESTOR']} professionalOnly={true}>
            <KanbanBoardPage />
          </RoleBasedRoute>
        } />
        <Route path="/:tenantSlug/professional/kanban" element={
          <RoleBasedRoute allowedRoles={['ATENDENTE', 'GESTOR']} professionalOnly={true}>
            <KanbanBoardPage />
          </RoleBasedRoute>
        } />
        <Route path="/professional/clients" element={
          <RoleBasedRoute allowedRoles={['ATENDENTE', 'GESTOR']} professionalOnly={true}>
            <ClientsPage />
          </RoleBasedRoute>
        } />
        <Route path="/professional/clients/new" element={
          <RoleBasedRoute allowedRoles={['ATENDENTE', 'GESTOR']} professionalOnly={true}>
            <ClientFormPage />
          </RoleBasedRoute>
        } />
        <Route path="/professional/clients/:clientId" element={
          <RoleBasedRoute allowedRoles={['ATENDENTE', 'GESTOR']} professionalOnly={true}>
            <ClientDetailPage />
          </RoleBasedRoute>
        } />
        <Route path="/professional/clients/:clientId/edit" element={
          <RoleBasedRoute allowedRoles={['ATENDENTE', 'GESTOR']} professionalOnly={true}>
            <ClientFormPage />
          </RoleBasedRoute>
        } />
        <Route path="/:tenantSlug/professional/clients" element={
          <RoleBasedRoute allowedRoles={['ATENDENTE', 'GESTOR']} professionalOnly={true}>
            <ClientsPage />
          </RoleBasedRoute>
        } />
        <Route path="/:tenantSlug/professional/clients/new" element={
          <RoleBasedRoute allowedRoles={['ATENDENTE', 'GESTOR']} professionalOnly={true}>
            <ClientFormPage />
          </RoleBasedRoute>
        } />
        <Route path="/:tenantSlug/professional/clients/:clientId" element={
          <RoleBasedRoute allowedRoles={['ATENDENTE', 'GESTOR']} professionalOnly={true}>
            <ClientDetailPage />
          </RoleBasedRoute>
        } />
        <Route path="/:tenantSlug/professional/clients/:clientId/edit" element={
          <RoleBasedRoute allowedRoles={['ATENDENTE', 'GESTOR']} professionalOnly={true}>
            <ClientFormPage />
          </RoleBasedRoute>
        } />
        
        {/* Shared Routes - Accessible by all authenticated users */}
        <Route path="/profile" element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        } />
        <Route path="/:tenantSlug/profile" element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        } />
        <Route path="/profile/edit" element={
          <ProtectedRoute>
            <EditProfilePage />
          </ProtectedRoute>
        } />
        <Route path="/:tenantSlug/profile/edit" element={
          <ProtectedRoute>
            <EditProfilePage />
          </ProtectedRoute>
        } />
        
        {/* Coming Soon Page for unimplemented features */}
        <Route path="/coming-soon" element={
          <ProtectedRoute>
            <ComingSoonPage />
          </ProtectedRoute>
        } />
        <Route path="/:tenantSlug/coming-soon" element={
          <ProtectedRoute>
            <ComingSoonPage />
          </ProtectedRoute>
        } />
        
        {/* Tenant Root Route - MUST be after all specific /:tenantSlug/* routes */}
        <Route path="/:tenantSlug" element={<TenantRootRoute />} />

        {/* Catch-all redirect */}
        <Route path="*" element={
          <Navigate to={getDefaultRedirectPath()} replace />
        } />
          </Routes>
        </div>
        </AppSettingsProvider>
      </ThemeProvider>
    </GoogleOAuthProvider>
  )
}

export default App